import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Wallet } from 'lucide-react';
import { z } from 'zod';
import { Form } from '@/components/ui/form';
import { PCMP_CORRECTION_ACTION_COPY } from '../../constants/pcmpCorrection.constants';
import {
  PCMP_ACCOUNT_KIND_BY_PAYMENT_TYPE,
  PCMP_MKT_COMMENT_MAX,
  PCMP_PAYMENT_TYPE_LABELS,
} from '../../constants/pcmpProposal.constants';
import { useCorrectPcmpProposalPayment } from '../../hooks/useCorrectPcmpProposalPayment';
import type { PcmpCorrectionFailure } from '../../types/pcmpCorrection.types';
import type { PcmpProposalDetail } from '../../types/pcmpProposal.types';
import { PcmpFailureSummary } from '../PcmpAccountsQueue/PcmpAccountsCells';
import { PcmpCommentField } from '../PcmpMarketingActions/PcmpCommentField';
import {
  buildPcmpAccountPayload,
  isPcmpAccountChanged,
  pcmpAccountDefaults,
  pcmpAccountFormShape,
} from '../../utils/pcmpAccountForm';
import { PcmpAccountFields } from '../PcmpProposalEdit/PcmpAccountFields';
import { PcmpEditDialog } from '../PcmpProposalEdit/PcmpEditDialog';
import { PcmpProposalDialogLoader } from './PcmpProposalDialogLoader';

const correctionFormSchema = z.object({
  ...pcmpAccountFormShape,
  comment: z.string().trim().min(1, 'A comment is required.').max(PCMP_MKT_COMMENT_MAX),
});

type CorrectionFormValues = z.infer<typeof correctionFormSchema>;

interface PcmpCorrectionPaymentDialogProps {
  proposalId: number;
  /** The failure being answered, when opened from the correction queue. */
  failure?: PcmpCorrectionFailure | null | undefined;
  onClose: () => void;
}

/** Legacy CorrectionPaymentEdit: only the account, for the payment type it already has. */
export function PcmpCorrectionPaymentDialog({
  proposalId,
  failure,
  onClose,
}: PcmpCorrectionPaymentDialogProps) {
  return (
    <PcmpProposalDialogLoader
      proposalId={proposalId}
      title={PCMP_CORRECTION_ACTION_COPY.EDIT.title}
      onClose={onClose}
    >
      {(proposal) => (
        <CorrectionForm proposal={proposal} failure={failure ?? null} onClose={onClose} />
      )}
    </PcmpProposalDialogLoader>
  );
}

interface CorrectionFormProps {
  proposal: PcmpProposalDetail;
  failure: PcmpCorrectionFailure | null;
  onClose: () => void;
}

function CorrectionForm({ proposal: openedWith, failure, onClose }: CorrectionFormProps) {
  // The saved account the diff compares against, as opened.
  const [proposal] = useState(openedWith);
  const { payment } = proposal;
  const kind = payment.account?.kind ?? PCMP_ACCOUNT_KIND_BY_PAYMENT_TYPE[payment.paymentType];
  const saved = pcmpAccountDefaults(payment.account);

  const form = useForm<CorrectionFormValues>({
    resolver: zodResolver(correctionFormSchema),
    defaultValues: { ...saved, comment: '' },
  });
  const { mutate, isPending, error } = useCorrectPcmpProposalPayment(proposal.proposalId, onClose);
  const copy = PCMP_CORRECTION_ACTION_COPY.EDIT;

  const onSubmit = form.handleSubmit((values) => {
    if (!kind) return;
    if (!isPcmpAccountChanged(kind, values, saved)) {
      form.setError('root', { message: 'Change at least one detail.' });
      return;
    }
    const account = buildPcmpAccountPayload(kind, values, (field, message) =>
      form.setError(field, { message }),
    );
    if (account) mutate({ account, comment: values.comment });
  });

  return (
    <Form {...form}>
      <PcmpEditDialog
        open
        onOpenChange={(open) => !open && onClose()}
        title={copy.title}
        description={proposal.refNo}
        icon={Wallet}
        isDirty={form.formState.isDirty}
        isPending={isPending}
        error={error}
        onSubmit={(event) => void onSubmit(event)}
        submitLabel={copy.submit}
        className="sm:max-w-2xl"
      >
        {failure ? (
          <div className="rounded-lg border bg-muted/40 px-4 py-3 text-sm">
            <PcmpFailureSummary
              stageLabel={failure.failureStageLabel}
              reason={failure.failureReason}
              by={failure.failedBy}
              at={failure.failedAt}
            />
          </div>
        ) : null}
        <p className="text-sm text-muted-foreground">
          Payment type: {PCMP_PAYMENT_TYPE_LABELS[payment.paymentType]}.
          {kind
            ? ' The corrected details are saved as a new account of the teacher; only this proposal moves to it. Approve the correction to send it back to Accounts.'
            : ' Cash and gift payments have no bank or wallet details to correct.'}
        </p>
        {kind ? <PcmpAccountFields control={form.control} kind={kind} /> : null}
        {form.formState.errors.root?.message ? (
          <p className="text-sm text-destructive" role="alert">
            {form.formState.errors.root.message}
          </p>
        ) : null}
        <PcmpCommentField
          control={form.control}
          name="comment"
          label="Comment"
          required
          placeholder="Why the details change"
        />
      </PcmpEditDialog>
    </Form>
  );
}
